import React, { useEffect, useRef, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import toast from "react-hot-toast";
import {
  X, Mail, Phone, Globe, Pencil, Trash2, Loader2, ChevronDown, ExternalLink, Bell, BellOff, Copy, UserPlus, UserMinus, MoreVertical,
} from "lucide-react";
import Drawer from "../overlays/Drawer";
import { Button, IconButton, Badge } from "../atoms";
import LeadFormModal from "./LeadFormModal";
import LeadTabs from "./LeadTabs";
import CallButton from "./calls/CallButton";
import FollowUpScheduler from "../molecules/FollowUpScheduler";
import EmailFollowUpPill from "../molecules/EmailFollowUpPill";
import CopyLeadModal from "./leads/CopyLeadModal";
import LostReasonModal from "../molecules/LostReasonModal";
import { useChangeLeadStatus, useDeleteLead, useUpdateLead, useAddCoOwner, useRemoveCoOwner } from "../hooks/useLeads";
import { useEmailAssistSegments } from "../hooks/useEmails";
import MemberPicker, { MemberPickerPanel } from "../molecules/MemberPicker";
import { useTeamMembers, formatMember, useMyCrmRole } from "../hooks/useTeamMembers";
import { resolveUserAvatar } from "../../../utils/userAvatar";
import { useOrgCurrency } from "../hooks/useOrgCurrency";
import { formatCurrency as libFormatCurrency } from "../lib/currency";
import { websiteHref as buildWebsiteHref } from "../lib/links";
import { useCrmBreakpoint } from "../hooks/useCrmBreakpoint";
import { Dropdown } from "antd";

// Click a field → edit in place. Blur or Enter saves, Esc cancels.
// Type "number" gets a numeric input; the parent decides what to send.
function InlineEdit({ value, onSave, type = "text", placeholder, render, style, inputStyle }) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState("");
  const [busy, setBusy] = useState(false);
  const inputRef = useRef(null);

  useEffect(() => {
    if (editing && inputRef.current) {
      inputRef.current.focus();
      inputRef.current.select?.();
    }
  }, [editing]);

  const begin = () => {
    setDraft(value == null ? "" : String(value));
    setEditing(true);
  };

  const commit = async () => {
    if (busy) return;
    const next = type === "number" ? (draft === "" ? null : Number(draft)) : draft.trim();
    const prev = type === "number" ? (value == null ? null : Number(value)) : (value || "");
    if (next === prev || (next === null && prev == null)) { setEditing(false); return; }
    setBusy(true);
    try { await onSave(next); setEditing(false); }
    catch (err) { toast.error(err?.response?.data?.error || err.message || "Save failed"); }
    finally { setBusy(false); }
  };

  if (editing) {
    return (
      <input
        ref={inputRef}
        type={type}
        value={draft}
        onChange={(e) => setDraft(e.target.value)}
        onBlur={commit}
        onKeyDown={(e) => {
          if (e.key === "Enter") { e.preventDefault(); commit(); }
          if (e.key === "Escape") { e.preventDefault(); setEditing(false); }
        }}
        disabled={busy}
        placeholder={placeholder}
        style={{
          background: "var(--color-bg-surface)",
          border: "1px solid var(--crm-primary)",
          borderRadius: 6,
          padding: "2px 6px",
          font: "inherit",
          color: "inherit",
          outline: "none",
          width: "100%",
          ...inputStyle,
        }}
      />
    );
  }

  return (
    <button
      type="button"
      onClick={begin}
      title="Click to edit"
      style={{
        background: "transparent", border: "1px solid transparent",
        borderRadius: 6, padding: "2px 6px", cursor: "text", font: "inherit",
        color: "inherit", letterSpacing: "inherit", textAlign: "left",
        transition: "background 100ms ease, border-color 100ms ease",
        ...style,
      }}
      onMouseEnter={(e) => {
        e.currentTarget.style.background = "var(--color-bg-muted)";
        e.currentTarget.style.borderColor = "var(--color-border-subtle)";
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.background = "transparent";
        e.currentTarget.style.borderColor = "transparent";
      }}
    >
      {render ? render(value) : (value == null || value === "" ? <span style={{ color: "var(--color-text-secondary)" }}>{placeholder || "—"}</span> : String(value))}
    </button>
  );
}

const statuses = [
  { key: "open", label: "Open", tone: "primary"  },
  { key: "hot",  label: "Hot",  tone: "danger"   },
  { key: "warm", label: "Warm", tone: "warning"  },
  { key: "cold", label: "Cold", tone: "info"     },
  { key: "won",  label: "Won",  tone: "success"  },
  { key: "lost", label: "Lost", tone: "danger"   },
];


function formatCurrency(n, ccy) {
  if (n == null) return "—";
  return libFormatCurrency(n, ccy || "USD") || `${ccy || "USD"} ${n}`;
}

function formatRelative(d) {
  if (!d) return "—";
  const diff = Date.now() - new Date(d).getTime();
  if (diff < 60_000) return "just now";
  if (diff < 3600_000) return `${Math.floor(diff / 60_000)}m ago`;
  if (diff < 86_400_000) return `${Math.floor(diff / 3600_000)}h ago`;
  if (diff < 7 * 86_400_000) return `${Math.floor(diff / 86_400_000)}d ago`;
  return new Date(d).toLocaleDateString();
}

function StatDivider({ show = true }) {
  if (!show) return null;
  return <div style={{ width: 1, height: 32, background: "var(--color-border-subtle)", flexShrink: 0 }} />;
}

function DrawerHeaderActions({
  isMobile,
  lead,
  onClose,
  onFollowUp,
  onEdit,
  onCopy,
  onDelete,
  followUpPending,
}) {
  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef(null);

  useEffect(() => {
    if (!menuOpen) return;
    const close = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) setMenuOpen(false);
    };
    document.addEventListener("mousedown", close);
    return () => document.removeEventListener("mousedown", close);
  }, [menuOpen]);

  const menuItemStyle = {
    display: "flex",
    alignItems: "center",
    gap: 10,
    width: "100%",
    padding: "10px 12px",
    border: "none",
    background: "transparent",
    cursor: "pointer",
    fontSize: 13,
    fontWeight: 500,
    color: "var(--color-text-primary)",
    textAlign: "left",
  };

  if (isMobile) {
    return (
      <div ref={menuRef} style={{ display: "flex", alignItems: "center", gap: 6, flexShrink: 0, position: "relative" }}>
        <CallButton leadId={lead._id} leadPhone={lead.phone} leadTitle={lead.title} />
        <IconButton aria-label="Close" onClick={onClose}><X size={17} /></IconButton>
        <IconButton aria-label="More actions" onClick={() => setMenuOpen((o) => !o)}>
          <MoreVertical size={16} />
        </IconButton>
        {menuOpen && (
          <div
            style={{
              position: "absolute",
              top: "calc(100% + 6px)",
              right: 0,
              zIndex: 10,
              minWidth: 200,
              background: "var(--color-bg-surface)",
              border: "1px solid var(--color-border-subtle)",
              borderRadius: 8,
              boxShadow: "0 8px 24px rgba(0,0,0,0.14)",
              padding: 4,
            }}
          >
            <button type="button" style={menuItemStyle} onClick={() => { setMenuOpen(false); onFollowUp(); }}>
              {followUpPending ? <Bell size={15} style={{ color: "var(--crm-primary)" }} /> : <BellOff size={15} />}
              Schedule follow-up
            </button>
            <button type="button" style={menuItemStyle} onClick={() => { setMenuOpen(false); onEdit(); }}>
              <Pencil size={15} /> Edit lead
            </button>
            <button type="button" style={menuItemStyle} onClick={() => { setMenuOpen(false); onCopy(); }}>
              <Copy size={15} /> Copy to project
            </button>
            <button
              type="button"
              style={{ ...menuItemStyle, color: "var(--crm-danger)" }}
              onClick={() => { setMenuOpen(false); onDelete(); }}
            >
              <Trash2 size={15} /> Delete lead
            </button>
          </div>
        )}
      </div>
    );
  }

  return (
    <div style={{ display: "flex", alignItems: "center", gap: 8, flexShrink: 0 }}>
      <CallButton leadId={lead._id} leadPhone={lead.phone} leadTitle={lead.title} />
      <IconButton aria-label="Schedule follow-up" onClick={onFollowUp}>
        {followUpPending
          ? <Bell size={15} style={{ color: "var(--crm-primary)" }} />
          : <BellOff size={15} />}
      </IconButton>
      <IconButton aria-label="Edit lead" onClick={onEdit}><Pencil size={15} /></IconButton>
      <IconButton aria-label="Copy lead to another project" onClick={onCopy}><Copy size={15} /></IconButton>
      <IconButton aria-label="Delete lead" onClick={onDelete}><Trash2 size={15} /></IconButton>
      <IconButton aria-label="Close" onClick={onClose}><X size={17} /></IconButton>
    </div>
  );
}

function HeroStat({ label, value, accent }) {
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 2, minWidth: 0 }}>
      <div style={{ fontSize: 12, color: "var(--color-text-muted)", fontWeight: 400 }}>
        {label}
      </div>
      <div style={{ fontSize: 15, fontWeight: 500, color: accent || "var(--color-text-primary)", lineHeight: "22px", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
        {value}
      </div>
    </div>
  );
}

function ContactQuickAction({ href, icon: Icon, label }) {
  if (!href) return null;
  return (
    <a
      href={href}
      style={{
        display: "inline-flex", alignItems: "center", gap: 6,
        padding: "6px 10px", borderRadius: 8,
        background: "var(--color-bg-muted)", color: "var(--color-text-primary)",
        fontSize: 12, fontWeight: 500, textDecoration: "none",
        transition: "background 120ms ease",
      }}
      onMouseEnter={(e) => { e.currentTarget.style.background = "var(--crm-primary-soft)"; }}
      onMouseLeave={(e) => { e.currentTarget.style.background = "var(--color-bg-muted)"; }}
    >
      <Icon size={12} /> {label}
    </a>
  );
}

// Vendor segment — which SOP the AI email assistant grounds its suggestions
// in for this vendor. Native <select> styled to match the drawer's fields;
// empty option clears it back to null. Persists via the standard lead update
// mutation (PATCH /leads/:id { vendorSegment }).
function VendorSegmentPicker({ lead }) {
  const { data: segments = [] } = useEmailAssistSegments();
  const updateLead = useUpdateLead();
  const [busy, setBusy] = useState(false);

  const onChange = async (e) => {
    const next = e.target.value || null;
    if (next === (lead.vendorSegment || null)) return;
    setBusy(true);
    try {
      await updateLead.mutateAsync({ id: lead._id, patch: { vendorSegment: next } });
    } catch (err) {
      toast.error(err?.response?.data?.error || err.message || "Save failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div style={{ minWidth: 0 }}>
      <div style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 11, color: "var(--color-text-secondary)", marginBottom: 4, fontWeight: 500 }}>
        Vendor segment
        {busy && <Loader2 size={11} className="animate-spin" />}
      </div>
      <select
        value={lead.vendorSegment || ""}
        onChange={onChange}
        disabled={busy}
        style={{
          width: "100%", fontSize: 13, padding: "5px 8px",
          border: "1px solid var(--color-border-subtle)", borderRadius: 6,
          background: "var(--color-bg-surface)", color: "var(--color-text-primary)",
          outline: "none", cursor: busy ? "wait" : "pointer",
        }}
      >
        <option value="">— None —</option>
        {segments.map((s) => (
          <option key={s.segmentKey} value={s.segmentKey}>
            {s.sopNumber != null ? `SOP #${s.sopNumber} — ` : ""}{s.title}
          </option>
        ))}
      </select>
    </div>
  );
}

function DetailsAccordion({ lead }) {
  const [open, setOpen] = useState(false);
  const { pagePad, isMobile } = useCrmBreakpoint();
  const labels = lead.labels || [];

  const rows = [
    { label: "Website", value: lead.website },
    { label: "Source", value: lead.source },
    { label: "Next activity", value: lead.nextActivity },
    { label: "Comments", value: lead.comments },
  ].filter((r) => r.value);

  return (
    <div style={{ borderTop: "1px solid var(--color-border-subtle)" }}>
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        style={{
          width: "100%", display: "flex", justifyContent: "space-between", alignItems: "center",
          padding: `10px ${pagePad}px`, background: "transparent", border: "none", cursor: "pointer",
          color: "var(--color-text-secondary)", fontSize: 12, fontWeight: 600,
          }}
      >
        <span>More details · {rows.length + (labels.length ? 1 : 0) + 1}</span>
        <motion.div animate={{ rotate: open ? 180 : 0 }} transition={{ duration: 0.18 }}>
          <ChevronDown size={14} />
        </motion.div>
      </button>
      <AnimatePresence initial={false}>
        {open && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.22, ease: [0.32, 0.72, 0, 1] }}
            style={{ overflow: "hidden" }}
          >
            <div style={{
              padding: `4px ${pagePad}px 16px`,
              display: "grid",
              gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr",
              gap: isMobile ? "12px 16px" : "12px 24px",
            }}>
              {rows.map((r) => (
                <div key={r.label} style={{ minWidth: 0 }}>
                  <div style={{ fontSize: 11, color: "var(--color-text-secondary)", marginBottom: 2, fontWeight: 500 }}>{r.label}</div>
                  <div style={{ fontSize: 13, color: "var(--color-text-primary)", wordBreak: "break-word", lineHeight: "18px" }}>{r.value}</div>
                </div>
              ))}
              {labels.length > 0 && (
                <div style={{ gridColumn: "1 / -1" }}>
                  <div style={{ fontSize: 11, color: "var(--color-text-secondary)", marginBottom: 6, fontWeight: 500 }}>Labels</div>
                  <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
                    {labels.map((t) => <Badge key={t} tone="neutral">{t}</Badge>)}
                  </div>
                </div>
              )}
              {/* Vendor segment — always shown; drives the AI email assistant. */}
              <VendorSegmentPicker lead={lead} />
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

function formatScheduled(d) {
  if (!d) return "—";
  const when = new Date(d);
  const now = Date.now();
  const diff = when.getTime() - now;
  const abs = Math.abs(diff);
  const sign = diff >= 0 ? "in " : "";
  const suffix = diff >= 0 ? "" : " ago";
  if (abs < 60 * 60 * 1000) return `${sign}${Math.max(1, Math.round(abs / 60_000))}m${suffix}`;
  if (abs < 24 * 60 * 60 * 1000) return `${sign}${Math.round(abs / 3600_000)}h${suffix}`;
  return when.toLocaleString([], { month: "short", day: "numeric", hour: "numeric", minute: "2-digit" });
}

// Co-owners panel — shown only when lead.status === 'won'. Collapsible to save
// space on mobile; list scrolls when many co-owners are assigned.
function CoOwnersPanel({ lead }) {
  const addMut = useAddCoOwner(lead._id);
  const removeMut = useRemoveCoOwner(lead._id);
  const { byId } = useTeamMembers();
  const { pagePad } = useCrmBreakpoint();
  const coOwners = lead.coOwners || [];
  const [open, setOpen] = useState(false);
  const [picking, setPicking] = useState(false);

  const excluded = new Set([
    ...coOwners.map((c) => String(c.userId)),
    lead.ownerId ? String(lead.ownerId) : null,
  ].filter(Boolean));

  const handleAdd = async (userId) => {
    if (!userId) return;
    const member = byId.get(String(userId));
    try {
      await addMut.mutateAsync({
        userId: String(userId),
        email: member?.email || '',
        name: formatMember(member),
      });
      setPicking(false);
      toast.success("Co-owner added");
    } catch (err) {
      toast.error(err?.response?.data?.error || err.message || "Failed");
    }
  };

  const handleRemove = async (userId) => {
    try {
      await removeMut.mutateAsync(String(userId));
      toast.success("Co-owner removed");
    } catch (err) {
      toast.error(err?.response?.data?.error || err.message || "Failed");
    }
  };

  const toggleAdd = (e) => {
    e.stopPropagation();
    setOpen(true);
    setPicking((p) => !p);
  };

  return (
    <div style={{
      margin: `0 ${pagePad}px`,
      background: "var(--color-bg-surface)",
      border: "1px solid var(--color-border-subtle)",
      borderRadius: 8,
      overflow: "hidden",
    }}>
      <div style={{ display: "flex", alignItems: "center", gap: 8, padding: "10px 14px" }}>
        <button
          type="button"
          onClick={() => setOpen((o) => !o)}
          style={{
            flex: 1,
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 8,
            padding: 0,
            border: "none",
            background: "transparent",
            cursor: "pointer",
            fontSize: 11,
            fontWeight: 600,
            color: "var(--color-text-secondary)",
            textAlign: "left",
          }}
        >
          <span>
            Co-owners{coOwners.length > 0 ? ` · ${coOwners.length}` : ""}
          </span>
          <motion.div animate={{ rotate: open ? 180 : 0 }} transition={{ duration: 0.18 }}>
            <ChevronDown size={14} />
          </motion.div>
        </button>
        <button
          type="button"
          onClick={toggleAdd}
          disabled={addMut.isPending}
          style={{
            display: "inline-flex", alignItems: "center", gap: 4,
            padding: "3px 10px", borderRadius: 6,
            border: "1px solid var(--color-border-subtle)",
            background: picking ? "var(--crm-primary-soft)" : "transparent",
            cursor: "pointer", fontSize: 12, fontWeight: 500,
            color: "var(--crm-primary)", flexShrink: 0,
          }}
        >
          <UserPlus size={11} />
          {addMut.isPending ? "Adding…" : "Add"}
        </button>
      </div>

      <AnimatePresence initial={false}>
        {open && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.22, ease: [0.32, 0.72, 0, 1] }}
            style={{ overflow: "hidden" }}
          >
            <div style={{ padding: "0 14px 12px" }}>
              {picking && (
                <div style={{ marginBottom: coOwners.length > 0 ? 10 : 0 }}>
                  <MemberPickerPanel
                    value={null}
                    allowClear={false}
                    onChange={(id) => { if (id && !excluded.has(String(id))) handleAdd(id); }}
                  />
                </div>
              )}

              {coOwners.length > 0 && (
                <div style={{
                  display: "flex",
                  flexDirection: "column",
                  gap: 4,
                  maxHeight: "min(220px, 35vh)",
                  overflowY: "auto",
                  WebkitOverflowScrolling: "touch",
                }}>
                  {coOwners.map((co) => {
                    const member = byId.get(String(co.userId));
                    const label = member ? formatMember(member) : (co.name || co.email || co.userId);
                    const coAvatar = member ? resolveUserAvatar(member) : null;
                    const initials = label.split(" ").map((w) => w[0]).slice(0, 2).join("").toUpperCase();
                    return (
                      <div
                        key={co.userId}
                        style={{
                          display: "flex", alignItems: "center", gap: 10,
                          padding: "6px 8px", borderRadius: 7,
                          background: "var(--color-bg-base)",
                          border: "1px solid var(--color-border-subtle)",
                        }}
                      >
                        {coAvatar ? (
                          <img src={coAvatar} alt="" style={{ width: 28, height: 28, borderRadius: "50%", flexShrink: 0, objectFit: "cover" }} />
                        ) : (
                        <span style={{
                          width: 28, height: 28, borderRadius: "50%", flexShrink: 0,
                          background: "var(--crm-primary-soft)", color: "var(--crm-primary)",
                          display: "flex", alignItems: "center", justifyContent: "center",
                          fontSize: 11, fontWeight: 600,
                        }}>
                          {initials}
                        </span>
                        )}
                        <span style={{
                          flex: 1, minWidth: 0, fontSize: 13, fontWeight: 500,
                          color: "var(--color-text-primary)",
                          overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap",
                        }}>
                          {label}
                        </span>
                        <button
                          type="button"
                          onClick={() => handleRemove(co.userId)}
                          disabled={removeMut.isPending}
                          title="Remove co-owner"
                          style={{
                            display: "flex", alignItems: "center", justifyContent: "center",
                            width: 24, height: 24, borderRadius: 6,
                            border: "none", background: "transparent",
                            cursor: "pointer", color: "var(--color-text-secondary)",
                            flexShrink: 0,
                          }}
                          onMouseEnter={(e) => { e.currentTarget.style.background = "var(--crm-danger-soft)"; e.currentTarget.style.color = "var(--crm-danger)"; }}
                          onMouseLeave={(e) => { e.currentTarget.style.background = "transparent"; e.currentTarget.style.color = "var(--color-text-secondary)"; }}
                        >
                          <UserMinus size={13} />
                        </button>
                      </div>
                    );
                  })}
                </div>
              )}

              {coOwners.length === 0 && !picking && (
                <p style={{ margin: 0, fontSize: 12, color: "var(--color-text-secondary)" }}>
                  No co-owners yet. Click Add to assign.
                </p>
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

export default function LeadDrawer({ open, onClose, lead }) {
  const [editing, setEditing] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [lostOpen, setLostOpen] = useState(false);
  const [actionError, setActionError] = useState(null);
  const [followUpOpen, setFollowUpOpen] = useState(false);
  const [copyOpen, setCopyOpen] = useState(false);

  const changeStatus = useChangeLeadStatus();
  const deleteLead = useDeleteLead();
  const updateLead = useUpdateLead();
  // Member name lookup for the "won by / lost by" attribution.
  const { byId: membersById } = useTeamMembers();
  const { currency: orgCurrency } = useOrgCurrency();
  const { isOwner } = useMyCrmRole();
  const { isMobile, pagePad } = useCrmBreakpoint();

  const saveField = (field) => async (next) => {
    await updateLead.mutateAsync({ id: lead._id, patch: { [field]: next } });
  };

  if (!lead) return null;
  const initialsSource = lead.brandName || lead.title || "?";
  const initials = initialsSource.split(" ").map((n) => n[0]).join("").slice(0, 2);

  const applyStatus = async (next, lostReason) => {
    setActionError(null);
    try {
      await changeStatus.mutateAsync({ id: lead._id, status: next, lostReason });
      setLostOpen(false);
    } catch (err) {
      setActionError(err?.response?.data?.error || err.message || "Status change failed");
    }
  };

  const onStatusClick = (next) => {
    if (next === lead.status) return;
    if (next === "lost") {
      setLostOpen(true);
      return;
    }
    applyStatus(next);
  };

  const onDeleteConfirm = async () => {
    setActionError(null);
    try {
      await deleteLead.mutateAsync(lead._id);
      setConfirmDelete(false);
      onClose?.();
    } catch (err) {
      setActionError(err?.response?.data?.error || err.message || "Delete failed");
    }
  };

  const websiteHref = buildWebsiteHref(lead.website);

  return (
    <>
      <Drawer open={open} onClose={onClose} width="min(1200px, 85vw)">
        <div style={{ display: "flex", flexDirection: "column", height: "100%", minHeight: 0 }}>
          {/* HEADER */}
          <header style={{
            display: "flex",
            alignItems: "flex-start",
            justifyContent: "space-between",
            padding: isMobile ? `14px ${pagePad}px 12px` : `20px ${pagePad}px 16px`,
            gap: isMobile ? 10 : 16,
            flexShrink: 0,
            borderBottom: isMobile ? "1px solid var(--color-border-subtle)" : undefined,
          }}>
            <div style={{ display: "flex", alignItems: "center", gap: isMobile ? 10 : 14, minWidth: 0, flex: 1 }}>
              <div style={{
                width: isMobile ? 36 : 40,
                height: isMobile ? 36 : 40,
                borderRadius: 8,
                background: "var(--crm-primary-soft)",
                color: "var(--crm-primary)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                fontWeight: 500,
                fontSize: 14,
                flexShrink: 0,
              }}>
                {initials}
              </div>
              <div style={{ minWidth: 0, flex: 1 }}>
                <div style={{ fontSize: isMobile ? 16 : 17, fontWeight: 600, color: "var(--color-text-primary)", lineHeight: "24px" }}>
                  <InlineEdit
                    value={lead.title}
                    onSave={saveField("title")}
                    placeholder="Untitled lead"
                    style={{ display: "block", padding: "0 6px", margin: "0 -6px" }}
                  />
                </div>
                <div style={{ fontSize: 13, color: "var(--color-text-secondary)", marginTop: 2 }}>
                  <InlineEdit
                    value={lead.brandName}
                    onSave={saveField("brandName")}
                    placeholder="Add brand / company"
                    style={{ display: "block", padding: "0 6px", margin: "0 -6px" }}
                  />
                </div>
              </div>
            </div>
            <DrawerHeaderActions
              isMobile={isMobile}
              lead={lead}
              onClose={onClose}
              onFollowUp={() => setFollowUpOpen(true)}
              onEdit={() => setEditing(true)}
              onCopy={() => setCopyOpen(true)}
              onDelete={() => setConfirmDelete(true)}
              followUpPending={lead.followUp?.status === "pending"}
            />
          </header>

          {/* Scrollable summary — stats, co-owners, quick actions, details */}
          <div style={{
            flexShrink: isMobile ? 0 : undefined,
            maxHeight: isMobile ? "min(42vh, 360px)" : undefined,
            overflowY: isMobile ? "auto" : "visible",
            WebkitOverflowScrolling: "touch",
          }}>
            {/* HERO STATS — value, status pills, last updated */}
            <div style={{
              padding: `0 ${pagePad}px 16px`,
              display: isMobile ? "grid" : "flex",
              gridTemplateColumns: isMobile ? "1fr 1fr" : undefined,
              alignItems: isMobile ? "stretch" : "center",
              gap: isMobile ? 12 : 24,
              flexWrap: isMobile ? undefined : "wrap",
            }}>
              <div style={{ display: "flex", flexDirection: "column", gap: 2, minWidth: 0 }}>
                <div style={{ fontSize: 12, color: "var(--color-text-muted)" }}>Value</div>
                <div style={{ fontSize: 15, fontWeight: 500, color: "var(--crm-primary)", lineHeight: "22px" }}>
                  <InlineEdit
                    value={lead.value}
                    type="number"
                    onSave={saveField("value")}
                    placeholder="Set value"
                    render={(v) => (v == null || v === "" ? <span style={{ color: "var(--color-text-secondary)", fontWeight: 500 }}>Set value</span> : formatCurrency(v, lead.currency || orgCurrency))}
                    inputStyle={{ fontSize: 16, fontWeight: 600, width: 140 }}
                  />
                </div>
              </div>
              <StatDivider show={!isMobile} />
              <div style={{ display: "flex", flexDirection: "column", gap: 4, gridColumn: isMobile ? "1 / -1" : undefined }}>
                <div style={{ fontSize: 12, color: "var(--color-text-muted)" }}>Status</div>
                <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                  <Dropdown
                    disabled={changeStatus.isPending}
                    menu={{
                      items: statuses.map((s) => ({ key: s.key, label: s.label })),
                      selectedKeys: [lead.status || "open"],
                      onClick: ({ key }) => onStatusClick(key),
                    }}
                    trigger={["click"]}
                  >
                      <button
                      type="button"
                      style={{
                        display: "inline-flex", alignItems: "center", gap: 6,
                        height: 32, padding: "0 12px",
                        borderRadius: 6,
                        border: "1px solid var(--color-border-strong)",
                        background: "var(--color-bg-surface)",
                        color: "var(--color-text-primary)",
                        fontSize: 14, fontWeight: 500,
                        cursor: changeStatus.isPending ? "wait" : "pointer",
                        opacity: changeStatus.isPending ? 0.6 : 1,
                      }}
                    >
                      {statuses.find((s) => s.key === (lead.status || "open"))?.label || "Open"}
                      <ChevronDown size={11} style={{ opacity: 0.6 }} />
                    </button>
                  </Dropdown>
                  {changeStatus.isPending && <Loader2 size={13} className="animate-spin" style={{ color: "var(--color-text-secondary)" }} />}
                </div>
              </div>
              {isOwner && (
                <>
                  <StatDivider show={!isMobile} />
                  <div style={{ display: "flex", flexDirection: "column", gap: 4, gridColumn: isMobile ? "1 / -1" : undefined }}>
                    <div style={{ fontSize: 12, color: "var(--color-text-muted)" }}>Owner</div>
                    <MemberPicker
                      value={lead.ownerId}
                      onChange={(id) => saveField("ownerId")(id)}
                      width={isMobile ? "100%" : 200}
                    />
                  </div>
                </>
              )}
              <StatDivider show={!isMobile} />
              <HeroStat label="Updated" value={formatRelative(lead.updatedAt)} />
              {lead.followUp?.status === "pending" && (
                <>
                  <StatDivider show={!isMobile} />
                  <button
                    type="button"
                    onClick={() => setFollowUpOpen(true)}
                    style={{ background: "transparent", border: "none", padding: 0, cursor: "pointer", textAlign: "left" }}
                  >
                    <HeroStat label="Follow-up" value={formatScheduled(lead.followUp.scheduledAt)} accent="var(--crm-primary)" />
                  </button>
                </>
              )}
              {lead.emailFollowUpSummary?.worstStatus && (
                <>
                  <StatDivider show={!isMobile} />
                  <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
                    <div style={{ fontSize: 12, color: "var(--color-text-muted)" }}>
                      Email follow-up
                    </div>
                    <EmailFollowUpPill summary={lead.emailFollowUpSummary} />
                  </div>
                </>
              )}
              {lead.status === "won" && (lead.wonAt || lead.wonBy) && (
                <>
                  <StatDivider show={!isMobile} />
                  <HeroStat
                    label="Won"
                    value={
                      <span>
                        {lead.wonBy ? formatMember(membersById.get(String(lead.wonBy))) : "—"}
                        {lead.wonAt && (
                          <span style={{ color: "var(--color-text-secondary)", fontWeight: 500 }}>
                            {" · "}{formatRelative(lead.wonAt)}
                          </span>
                        )}
                      </span>
                    }
                    accent="var(--crm-success, rgb(16,185,129))"
                  />
                </>
              )}
              {lead.status === "lost" && (lead.lostAt || lead.lostBy) && (
                <>
                  <StatDivider show={!isMobile} />
                  <HeroStat
                    label="Lost"
                    value={
                      <span>
                        {lead.lostBy ? formatMember(membersById.get(String(lead.lostBy))) : "—"}
                        {lead.lostAt && (
                          <span style={{ color: "var(--color-text-secondary)", fontWeight: 500 }}>
                            {" · "}{formatRelative(lead.lostAt)}
                          </span>
                        )}
                      </span>
                    }
                    accent="var(--crm-danger)"
                  />
                </>
              )}
              {lead.status === "lost" && lead.lostReason && (
                <>
                  <StatDivider show={!isMobile} />
                  <HeroStat label="Lost reason" value={lead.lostReason} />
                </>
              )}
            </div>

            {lead.status === "won" && (
              <div style={{ paddingTop: 12, paddingBottom: 4 }}>
                <CoOwnersPanel lead={lead} />
              </div>
            )}

            {(lead.email || lead.phone || websiteHref) && (
              <div style={{ padding: `0 ${pagePad}px 16px`, display: "flex", gap: 8, flexWrap: "wrap" }}>
                <ContactQuickAction href={lead.email ? `mailto:${lead.email}` : null} icon={Mail} label={lead.email || ""} />
                <ContactQuickAction href={lead.phone ? `tel:${lead.phone}` : null} icon={Phone} label={lead.phone || ""} />
                {websiteHref && (
                  <a
                    href={websiteHref}
                    target="_blank"
                    rel="noreferrer"
                    style={{
                      display: "inline-flex", alignItems: "center", gap: 6,
                      padding: "6px 10px", borderRadius: 8,
                      background: "var(--color-bg-muted)", color: "var(--color-text-primary)",
                      fontSize: 12, fontWeight: 500, textDecoration: "none",
                    }}
                  >
                    <Globe size={12} /> {lead.website} <ExternalLink size={10} style={{ opacity: 0.6 }} />
                  </a>
                )}
              </div>
            )}

            <AnimatePresence>
              {actionError && (
                <motion.div
                  initial={{ height: 0, opacity: 0 }}
                  animate={{ height: "auto", opacity: 1 }}
                  exit={{ height: 0, opacity: 0 }}
                  transition={{ duration: 0.18 }}
                  style={{ overflow: "hidden" }}
                >
                  <div style={{ padding: `10px ${pagePad}px`, background: "var(--crm-danger-soft)", color: "var(--crm-danger)", fontSize: 13, display: "flex", justifyContent: "space-between", alignItems: "center", gap: 12 }}>
                    <span>{typeof actionError === "string" ? actionError : "Action failed"}</span>
                    <button onClick={() => setActionError(null)} style={{ background: "transparent", border: "none", color: "currentColor", cursor: "pointer", fontSize: 12, opacity: 0.7 }}>Dismiss</button>
                  </div>
                </motion.div>
              )}
            </AnimatePresence>

            <DetailsAccordion lead={lead} />
          </div>

          {/* TABS — full width, primary focus */}
          <div style={{ flex: 1, minHeight: 0, display: "flex", flexDirection: "column", borderTop: "1px solid var(--color-border-subtle)" }}>
            <LeadTabs leadId={lead._id} lead={lead} />
          </div>

          <AnimatePresence>
            {confirmDelete && (
              <motion.div
                initial={{ y: 60, opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                exit={{ y: 60, opacity: 0 }}
                transition={{ duration: 0.2, ease: [0.32, 0.72, 0, 1] }}
                style={{ padding: `14px ${pagePad}px`, borderTop: "1px solid var(--color-border-subtle)", background: "var(--crm-danger-soft)", flexShrink: 0 }}
              >
                <div style={{ fontSize: 13, color: "var(--crm-danger)", marginBottom: 10 }}>
                  Delete this lead?
                </div>
                <div style={{ display: "flex", justifyContent: "flex-end", gap: 8 }}>
                  <Button variant="secondary" size="md" onClick={() => setConfirmDelete(false)} disabled={deleteLead.isPending}>Cancel</Button>
                  <Button variant="danger" size="md" onClick={onDeleteConfirm} disabled={deleteLead.isPending} leftIcon={deleteLead.isPending ? <Loader2 size={15} className="animate-spin" /> : null}>
                    Delete
                  </Button>
                </div>
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </Drawer>

      <LeadFormModal
        open={editing}
        onClose={() => setEditing(false)}
        lead={lead}
        onSaved={() => setEditing(false)}
      />

      <FollowUpScheduler
        open={followUpOpen}
        lead={lead}
        onClose={() => setFollowUpOpen(false)}
      />

      <CopyLeadModal
        open={copyOpen}
        onClose={() => setCopyOpen(false)}
        lead={lead}
      />

      <LostReasonModal
        open={lostOpen}
        onClose={() => setLostOpen(false)}
        onSubmit={(reason) => applyStatus("lost", reason)}
        isPending={changeStatus.isPending}
        title={`Mark "${lead?.title || "lead"}" as lost`}
      />
    </>
  );
}
